import { useEffect, useRef, useState, useSyncExternalStore, type ReactElement } from "react";
import { Button, Glyph, logical } from "../../ui/kit/index.ts";
import { classes } from "../../ui/kit/classes.ts";
import { isTextField } from "../../ui/input/text-field.ts";
import { PIXEL_FACES, TICK_MS } from "odyssey-shared";
import { deathRemark } from "./remark.ts";
import { RESPAWN_HOLD_MS, respawnReady, secondsLeft, type DeathScreen, type DeathShown } from "./death-screen.ts";
import { say } from "../../words.ts";

/**
 * The death screen (ADR-073's amendment "the respawn is the player's, and a death is said in chat", decision 8): a
 * full-screen overlay in the Dark Souls manner over the world, under the chat and the HUD. The world beneath is
 * dimmed and drained of colour; a dark band across the middle holds "You died" in the title face at its native size in
 * the danger colour, the cause line in the pixel face, under it what the death cost (P2.5-T7: one line a penalty, the
 * design's debuff row, its hourglass and its words in the danger colour), a remark on the death when it has one
 * (`deathRemark`; none today), the countdown, and the button that asks for
 * the respawn. The
 * overlay takes the pointer, so a click on the world does nothing while it shows. A death seen live fades it in over
 * 25 motion frames, stepped, which reduced motion drops.
 */

/**
 * When the screen next changes what it draws by time alone: the next whole second of the countdown, the button's time a
 * tick after it ends, or the end of a request's hold.
 *
 * @param shown The screen.
 * @param nowMs Page time in ms.
 * @returns The moment in page time, or null when nothing changes by time.
 */
function nextChangeAt(shown: DeathShown, nowMs: number): number | null {
  if (shown.readyAtMs !== null && nowMs < shown.readyAtMs - TICK_MS) {
    const left = shown.readyAtMs - TICK_MS - nowMs;
    return nowMs + (left % 1000 === 0 ? 1000 : left % 1000);
  }
  if (shown.readyAtMs !== null && nowMs < shown.readyAtMs) {
    return shown.readyAtMs;
  }
  if (shown.requestedAtMs !== null && nowMs - shown.requestedAtMs < RESPAWN_HOLD_MS) {
    return shown.requestedAtMs + RESPAWN_HOLD_MS;
  }
  return null;
}

/**
 * The page's time now, which the countdown reads.
 *
 * @returns `performance.now()`.
 */
function pageNow(): number {
  return performance.now();
}

/**
 * The overlay.
 *
 * @param props The screen's store.
 * @returns The overlay while the character is dead, else nothing.
 */
export function DeathOverlay({ store }: { readonly store: DeathScreen }): ReactElement | null {
  const shown = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
  const [now, setNow] = useState(pageNow);
  const action = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const t = pageNow();
    const at = shown === null ? null : nextChangeAt(shown, t);
    if (at === null) {
      return undefined;
    }
    const timer = setTimeout(() => setNow(pageNow()), Math.ceil(at - t) + 1);
    return () => clearTimeout(timer);
  }, [shown, now]);
  const ready = shown !== null && respawnReady(shown, Math.max(now, pageNow()));
  useEffect(() => {
    store.drawnReady = ready;
    if (ready && !isTextField(document.activeElement)) {
      action.current?.querySelector("button")?.focus();
    }
  }, [ready, store]);
  if (shown === null) {
    return null;
  }
  const remark = deathRemark(shown);
  const seconds = secondsLeft(shown, Math.max(now, pageNow()));
  const title = PIXEL_FACES.title;
  return (
    <div className={classes("ot-death", shown.fade && "is-fading")} key={shown.serial} role="alertdialog" aria-modal="false" aria-labelledby="ot-death-title" aria-describedby={shown.costs.length > 0 ? "ot-death-cause ot-death-costs" : "ot-death-cause"}>
      <div className="ot-death__band">
        <h1 id="ot-death-title" className="ot-death__title" style={{ fontSize: logical(title.nativeSize), lineHeight: logical(title.lineHeight) }}>
          {say("death.screen.title")}
        </h1>
        <p id="ot-death-cause" className="ot-death__cause">
          {shown.cause ?? ""}
        </p>
        {shown.costs.length > 0 ? (
          <ul id="ot-death-costs" className="ot-death__costs">
            {shown.costs.map((line) => (
              <li key={line} className="ot-death__cost" data-death="cost">
                <Glyph name="hourglass" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        ) : null}
        {remark === null ? null : <p className="ot-death__remark">{remark}</p>}
        <p className="ot-death__countdown" aria-live="polite">
          {seconds > 0 ? say("death.screen.countdown", { seconds: String(seconds) }) : ""}
        </p>
        <div className="ot-death__action" ref={action}>
          <Button variant="primary" disabled={!ready} onClick={() => store.press(pageNow())}>
            {shown.place === null ? say("death.screen.respawn") : say("death.screen.respawnAt", { place: shown.place })}
          </Button>
        </div>
      </div>
    </div>
  );
}
